import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Layout,
  Menu,
  Dropdown,
  Space,
  Breadcrumb,
  Drawer,
  Divider,
  Modal,
  Row,
  Col,
  Spin,
} from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { CaretDownFilled, MenuOutlined } from "@ant-design/icons";
import "./style.css";
import { Footer } from "antd/es/layout/layout";
import {
  breadcrumbList,
  dashboardsList,
  displaysList,
  footerText,
  userPages,
} from "../Common/Utils";
import {
  BREADCRUMBS,
  ROLES,
  ORG_TYPES,
  ROUTES,
  LIVE_PAGE_LIST,
  DISPLAYS_MENU_LIST,
  DASHBOARD_MENU_LIST,
  USER_MENU_LIST,
} from "../Common/Constants";
import { useOrganization } from "../../OrganizationProvider";
import jwtDecode from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import {
  EOLConfirmationPopup,
  WithoutOrganisationPopup,
} from "../Common/confirmationPopup";
import { setNavStop } from "../../reducers/authSlice";
import CustomLink from "../Common/CustomLink";
import ChangeOrganisation from "../Common/ChangeOrganisationModal";
import LastUpdatedStatus from "../Common/LastUpdatedStatus";
import VEHICLE from "../../assets/images/icons/outline-vehicle.png";
import { toast } from "react-toastify";

const { Header, Content } = Layout;
const vehicleIcon = <img src={VEHICLE} alt="#" className="vehicle-loader" />;

const AuthLayout = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const organization = useOrganization();
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const currentRole = userDetails?.currentRole;
  const mainOrganisation = userDetails?.mainOrganisation;
  const urlElements = location.pathname.split("/");
  let lastValue = urlElements[urlElements.length - 1];
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const userLinks = userPages(JSON.parse(localStorage.getItem("userDetails")));
  const decodedJwt = localStorage.getItem("userToken")
    ? jwtDecode(localStorage.getItem("userToken"))
    : "";

  const navStop = useSelector((state) => state.auth)?.navStop;
  const pageStatus = useSelector((state) => state.loader)?.pageLoader;
  const pageLastUpdatedTime = useSelector(
    (state) => state.loader
  )?.pageLastUpdatedTime;
  const [modal, contextHolder] = Modal.useModal();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sessionTimeout = useCallback(() => {
    const isTokenExpired = Math.floor(Date.now() / 1000) >= decodedJwt?.exp;
    if (isTokenExpired) {
      toast.info("Session Expired! Please Login", {
        autoClose: 3000,
        hideProgressBar: false,
        theme: "light",
      });
      localStorage.clear();
      window.location.reload();
    }
  }, [decodedJwt?.exp]);

  useEffect(() => {
    const now = dayjs();
    const tokenExp = dayjs(decodedJwt?.exp * 1000);

    setTimeout(() => {
      sessionTimeout();
    }, tokenExp.diff(now, "ms"));

    if (
      userDetails?.mainOrganisation?.type === ORG_TYPES.ADMINISTRATOR &&
      userDetails?.otherOrganisations?.length === 0
    ) {
      modal.error({
        ...WithoutOrganisationPopup,
        onOk: () => navigate("/logout"),
      });
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, [sessionTimeout]);

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const onConfirmation = async (to) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      if (to === "/" + ROUTES.CHANGE_ORG.key) {
        showCommentModal();
      } else {
        dispatch(setNavStop(false));
        navigate(to);
      }
    }
  };

  const onDropdownClick = async (e) => {
    const confirmation = navStop
      ? await modal.confirm(EOLConfirmationPopup)
      : true;
    if (confirmation) {
      if (e.key === ROUTES.CHANGE_ORG.key) {
        showCommentModal();
      } else {
        dispatch(setNavStop(false));
        navigate(`/${e.key}`);
      }
    }
  };

  const showCommentModal = () => {
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {contextHolder}
      <Header className="header">
        {isModalOpen && (
          <ChangeOrganisation
            isModalOpen={isModalOpen}
            handleCloseModal={handleCloseModal}
          />
        )}
        <CustomLink to={"/"} navStop={navStop} onConfirmation={onConfirmation}>
          <img
            src={`${organization.s3BucketBaseUrl}/InnerLogo.svg`}
            className="logo"
            height={32}
            alt="Logo"
          />
        </CustomLink>

        {isMobile ? (
          <>
            <MenuOutlined
              onClick={toggleDrawer}
              data-testid="mobile-menu"
              className="mob-menu"
            />

            <Drawer
              title={
                <div className="flex">
                  {`${mainOrganisation?.name} - ${currentRole}`}
                </div>
              }
              placement="right"
              closable={true}
              onClose={toggleDrawer}
              open={isDrawerOpen}
            >
              {ROLES.FLEET_MANAGER !== currentRole ? (
                <div className="drawer-pd">
                  <CustomLink
                    to={ROUTES.HOME.link}
                    navStop={navStop}
                    onConfirmation={onConfirmation}
                  >
                    <span>{BREADCRUMBS.HOME}</span>
                  </CustomLink>
                </div>
              ) : null}

              {ROLES.ERPUSER === currentRole ? (
                <div className="drawer-pd">
                  <CustomLink
                    to={ROUTES.VEHICLE_INFORMATION.key}
                    navStop={navStop}
                    onConfirmation={onConfirmation}
                  >
                    <span>{ROUTES.VEHICLE_INFORMATION.title}</span>
                  </CustomLink>
                </div>
              ) : null}

              {dashboardsList().map((item, index) => (
                <div key={index} className="drawer-pd">
                  <CustomLink
                    to={`/${item.key}`}
                    navStop={navStop}
                    onConfirmation={onConfirmation}
                  >
                    <span>{item.label}</span>
                  </CustomLink>
                </div>
              ))}

              {displaysList().map((item, index) => (
                <div key={index} className="drawer-pd">
                  <a href={`/${item.key}`}>
                    <span>{item.label}</span>
                  </a>
                </div>
              ))}

              {ROLES.FLEET_MANAGER !== currentRole ? (
                <div className="drawer-pd">
                  <CustomLink
                    to={ROUTES.EXPORT.link}
                    navStop={navStop}
                    onConfirmation={onConfirmation}
                  >
                    <span>{BREADCRUMBS.EXPORT}</span>
                  </CustomLink>
                </div>
              ) : null}
              {ROLES.FLEET_MANAGER !== currentRole ? (
                <div className="drawer-pd">
                  <CustomLink
                    to={ROUTES.ALERTS.link}
                    navStop={navStop}
                    onConfirmation={onConfirmation}
                  >
                    <span>{BREADCRUMBS.ALERTS}</span>
                  </CustomLink>
                </div>
              ) : null}

              <Divider />
              {userLinks.map(
                (item, index) =>
                  item.key && (
                    <div key={index} className="drawer-pd">
                      <CustomLink
                        to={`/${item.key}`}
                        navStop={navStop}
                        onConfirmation={onConfirmation}
                      >
                        <span>{item.label}</span>
                      </CustomLink>
                    </div>
                  )
              )}
            </Drawer>
          </>
        ) : (
          <>
            <Menu mode="horizontal" className="ml-auto" items={[]} />
            {ROLES.FLEET_MANAGER !== currentRole ? (
              <CustomLink
                to={ROUTES.HOME.link}
                className="mr2"
                navStop={navStop}
                onConfirmation={onConfirmation}
              >
                <Space
                  className={
                    "ml-auto flex-center " +
                    (ROUTES.HOME.key === lastValue ? "active-menu" : "white")
                  }
                >
                  <span className="mt025 display-flex">{BREADCRUMBS.HOME}</span>
                </Space>
              </CustomLink>
            ) : null}
            {ROLES.ERPUSER === currentRole ? (
              <CustomLink
                to={ROUTES.VEHICLE_INFORMATION.link}
                className="mr2"
                navStop={navStop}
                onConfirmation={onConfirmation}
              >
                <Space
                  className={
                    "ml-auto flex-center " +
                    (ROUTES.VEHICLE_INFORMATION.key === lastValue
                      ? "active-menu"
                      : "white")
                  }
                >
                  <span className="mt025 display-flex">
                    {ROUTES.VEHICLE_INFORMATION.title}
                  </span>
                </Space>
              </CustomLink>
            ) : null}

            {dashboardsList()?.length > 0 ? (
              <Dropdown
                className="mr1"
                menu={{
                  onClick: onDropdownClick,
                  selectedKeys: lastValue,
                  selectable: true,
                  items: dashboardsList(),
                }}
              >
                <Space
                  className={
                    DASHBOARD_MENU_LIST.includes(lastValue)
                      ? " active-menu"
                      : " white"
                  }
                >
                  <span className="mt025 display-flex">
                    {BREADCRUMBS.DASHBOARDS}
                    <CaretDownFilled className="op08" />
                  </span>
                </Space>
              </Dropdown>
            ) : null}

            {displaysList()?.length > 0 ? (
              <Dropdown
                className="mr1"
                menu={{
                  onClick: onDropdownClick,
                  selectedKeys: lastValue,
                  selectable: true,
                  items: displaysList(),
                }}
              >
                <Space
                  className={
                    DISPLAYS_MENU_LIST.includes(lastValue)
                      ? "active-menu"
                      : "white"
                  }
                >
                  <span className="mt025 display-flex">
                    {BREADCRUMBS.DISPLAYS}
                    <CaretDownFilled className="op08" />
                  </span>
                </Space>
              </Dropdown>
            ) : null}

            {ROLES.ERPUSER !== currentRole &&
            ROLES.FLEET_MANAGER !== currentRole ? (
              <>
                <CustomLink
                  to={ROUTES.EXPORT.link}
                  className="mr2"
                  navStop={navStop}
                  onConfirmation={onConfirmation}
                >
                  <Space
                    className={
                      "ml-auto flex-center " +
                      (ROUTES.EXPORT.key === lastValue
                        ? "active-menu"
                        : "white")
                    }
                  >
                    <span className="mt025 display-flex">
                      {BREADCRUMBS.EXPORT}
                    </span>
                  </Space>
                </CustomLink>

                <CustomLink
                  to={ROUTES.ALERTS.link}
                  className="mr2"
                  navStop={navStop}
                  onConfirmation={onConfirmation}
                >
                  <Space
                    className={
                      "ml-auto flex-center " +
                      (ROUTES.ALERTS.key === lastValue
                        ? "active-menu"
                        : "white")
                    }
                  >
                    <span className="mt025 display-flex">
                      {BREADCRUMBS.ALERTS}
                    </span>
                  </Space>
                </CustomLink>
              </>
            ) : null}

            <Dropdown
              menu={{
                onClick: onDropdownClick,
                selectedKeys: lastValue,
                selectable: true,
                items: userLinks,
              }}
            >
              <Space
                data-testid="user-menu"
                className={
                  USER_MENU_LIST.includes(lastValue) ? "active-menu" : "white"
                }
              >
                {/* <UserOutlined className="icon-margin icon-font20" /> */}
                <span className="mt025 display-flex">
                  {`${userDetails?.firstName}`}
                  <CaretDownFilled className="op08" />
                </span>
              </Space>
            </Dropdown>
          </>
        )}
      </Header>
      <Row className="breadcrumb-layout" justify={"space-between"}>
        <Col>
          {lastValue !== ROUTES.GRAFANA_LOGIN.key &&
            lastValue !== ROUTES.ACCESS_DENIED.key && (
              <Breadcrumb separator=">" items={breadcrumbList(lastValue)} />
            )}
        </Col>
        <Col className="updated-time-layout">
          {LIVE_PAGE_LIST.includes(lastValue) && (
            <LastUpdatedStatus
              showTime={"time-ago"}
              lastUpdatedTime={pageLastUpdatedTime}
            />
          )}
        </Col>
      </Row>
      <Layout className="layout">
        <Spin
          indicator={vehicleIcon}
          spinning={pageStatus === "refresh"}
          size="large"
        >
          <Content className="content-layout">{props.children}</Content>
        </Spin>
      </Layout>
      <Footer className="footer">{footerText}</Footer>
    </Layout>
  );
};

export default AuthLayout;
